import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SHOP } from "@/constants/testIds";

export default function Shop() {
  const { slug } = useParams(); // category slug
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("q") || "";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState(initialSearch);
  const [selectedCat, setSelectedCat] = useState(slug || "all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setSelectedCat(slug || "all");
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (selectedCat && selectedCat !== "all") params.category = selectedCat;
    if (search) params.search = search;
    const t = setTimeout(() => {
      api
        .get("/products", { params })
        .then((r) => setProducts(r.data))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [selectedCat, search]);

  const category = categories.find((c) => c.slug === selectedCat);

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16">
      <div className="mb-12">
        <p className="overline text-emerald-800 mb-3">
          {category ? category.name : "Full catalog"}
        </p>
        <h1 className="font-display text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
          {category ? category.name : "All Hospital Supplies"}
        </h1>
        {category && (
          <p className="mt-4 text-slate-600 max-w-2xl">{category.description}</p>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Sidebar filters */}
        <aside className="lg:col-span-3">
          <div className="sticky top-24 space-y-8">
            <div>
              <p className="overline text-slate-500 mb-3">Search</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid={SHOP.search}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setSearchParams({ q: e.target.value });
                  }}
                  placeholder="Gloves, masks, gauze..."
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-900/20 focus:border-emerald-900"
                />
              </div>
            </div>

            <div>
              <p className="overline text-slate-500 mb-3">Category</p>
              <div className="space-y-1" data-testid={SHOP.categoryFilter}>
                <FilterOption
                  active={selectedCat === "all"}
                  label="All products"
                  onClick={() => (window.location.href = "/shop")}
                />
                {categories.map((c) => (
                  <FilterOption
                    key={c.slug}
                    active={selectedCat === c.slug}
                    label={c.name}
                    onClick={() => (window.location.href = `/category/${c.slug}`)}
                  />
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-9">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-slate-100 aspect-[3/4] rounded-sm animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center">
              <p className="font-display text-xl text-slate-900 mb-2">No products found</p>
              <p className="text-sm text-slate-500">Try a different search or category.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-6">
                Showing <span className="font-semibold text-slate-900">{products.length}</span> products
              </p>
              <div
                data-testid={SHOP.grid}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterOption({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${
        active
          ? "bg-emerald-900 text-white"
          : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      {label}
    </button>
  );
}
